import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Global_Data } from "../../../globalData/GlobalData";
import { Box, Paper } from "@mui/material";
import { useDroppable } from "@dnd-kit/core";
import { Rnd } from "react-rnd";
import { baseURL } from "../../../api";
import { useTheme } from "@mui/material/styles";
import { getUnavailableElementFieldPaths } from "../reportFieldAvailability";

function resolveDesignerColor(theme, value) {
  const key = String(value || "").trim();

  if (!key) return "inherit";

  const map = {
    "text.primary": theme?.palette?.text?.primary,
    "text.secondary": theme?.palette?.text?.secondary,
    "primary.main": theme?.palette?.primary?.main,
    "secondary.main": theme?.palette?.secondary?.main,
    "error.main": theme?.palette?.error?.main,
    "warning.main": theme?.palette?.warning?.main,
    "info.main": theme?.palette?.info?.main,
    "success.main": theme?.palette?.success?.main,
    transparent: "transparent",
    inherit: "inherit",
    black: "#000000",
    white: "#ffffff"
  };

  return map[key] || key;
}
  
  
export default function ReportCanvas({
  template,
  previewData = {},
  availableFieldPaths = [],
  fieldValidationEnabled = false,
  selectedIds = [],
  onSelectionChange,
  onUpdateElement,
  onUpdateElements,
  onDeleteElements,
  readOnly = false
}) {
  const { setNodeRef } = useDroppable({
    id: "report-canvas"
  });
  const canvasRef = useRef(null);
  const groupDragRef = useRef(null);
  const selectionBoxRef = useRef(null);
  const [selectionBox, setSelectionBox] = useState(null);
  const [groupDrag, setGroupDrag] = useState(null);

  const zoom = 1.6;
  const pageWidth = template?.page?.width || 595;
  const pageHeight = template?.page?.height || 842;
  const elements = template?.elements || [];
  const selectedIdSet = new Set(selectedIds);
  const unavailableFieldPathsByElementId = new Map(
    elements.map(element => [
      element.id,
      fieldValidationEnabled
        ? getUnavailableElementFieldPaths(element, availableFieldPaths)
        : []
    ])
  );

  const bandDefinitions = [
  {
    type: "pageHeaderBand",
    childBand: "pageHeader",
    label: "Page Header"
  },
  {
    type: "pageFooterBand",
    childBand: "pageFooter",
    label: "Page Footer"
  },
  {
    type: "groupHeaderBand",
    childBand: "groupHeader",
    label: "Group Header"
  },
  {
    type: "groupFooterBand",
    childBand: "groupFooter",
    label: "Group Footer"
  },
  {
    type: "summaryBand",
    childBand: "summary",
    label: "Summary Band"
  }
];

const bandTypes = new Set(
  bandDefinitions.map(item => item.type)
);

const bandBackground = {
    pageHeaderBand:
        "rgba(25,118,210,.05)",

    pageFooterBand:
        "rgba(25,118,210,.05)",

    groupHeaderBand:
        "rgba(46,125,50,.05)",

    groupFooterBand:
        "rgba(46,125,50,.05)",

    summaryBand:
        "rgba(156,39,176,.06)"
};

const findBandForChild = element =>
  bandDefinitions
    .map(definition => ({
      definition,
      band: elements.find(
        item => item.type === definition.type
      )
    }))
    .find(
      item =>
        item.band &&
        element.band === item.definition.childBand
    ) || null;


  const summaryBand = elements.find(
    element => element.type === "summaryBand"
  );

  const theme = useTheme();

  const getDisplayX = element => {
  const parentBand = findBandForChild(element);

  if (parentBand) {
    return (
      (parentBand.band.x || 0) +
      (element.x || 0)
    );
  }

  return element.x || 0;
  };

  const getDisplayY = element => {
    const parentBand = findBandForChild(element);

    if (parentBand) {
      return (
        (parentBand.band.y || 0) +
        (element.y || 0)
      );
    }

  return element.y || 0;
  };

  const getElementDisplayHeight = element => {
    const isRepeatingSummary =
      element.type === "summaryTaxList" ||
      element.type === "summaryWithholdingList";

    if (!isRepeatingSummary) {
      return element.height || 30;
    }

    const rows = resolveRawPreviewValue(
      element.fieldPath,
      previewData
    );

    const rowCount =
      Array.isArray(rows) && rows.length > 0
        ? rows.length
        : 1;

    return Math.max(
      element.height || 24,
      rowCount * (element.rowHeight || 18)
    );
  };

  const getEffectiveMoveIds = ids => {
    const idSet = new Set(ids);

    return ids.filter(id => {
      const element = elements.find(item => item.id === id);
      const parentBand = element ? findBandForChild(element) : null;

      // Moving a selected band already moves its children visually. Keeping a
      // selected child's relative coordinates unchanged prevents double moves.
      return element && !(parentBand && idSet.has(parentBand.band.id));
    });
  };

  const clampSelectionDelta = (ids, requestedX, requestedY) => {
    const effectiveIds = getEffectiveMoveIds(ids);
    let minimumX = Number.NEGATIVE_INFINITY;
    let maximumX = Number.POSITIVE_INFINITY;
    let minimumY = Number.NEGATIVE_INFINITY;
    let maximumY = Number.POSITIVE_INFINITY;

    effectiveIds.forEach(id => {
      const element = elements.find(item => item.id === id);
      if (!element) return;

      const parentBand = findBandForChild(element);
      const widthLimit = parentBand ? (parentBand.band.width || pageWidth) : pageWidth;
      const heightLimit = parentBand ? (parentBand.band.height || pageHeight) : pageHeight;
      const x = element.x || 0;
      const y = element.y || 0;
      const width = element.width || 120;
      const height = getElementDisplayHeight(element);

      minimumX = Math.max(minimumX, -x);
      maximumX = Math.min(maximumX, widthLimit - width - x);
      minimumY = Math.max(minimumY, -y);
      maximumY = Math.min(maximumY, heightLimit - height - y);
    });

    return {
      x: Math.max(minimumX, Math.min(requestedX, maximumX)),
      y: Math.max(minimumY, Math.min(requestedY, maximumY))
    };
  };

  const moveSelection = (ids, requestedX, requestedY) => {
    if (readOnly || ids.length === 0) return;

    const delta = clampSelectionDelta(ids, requestedX, requestedY);
    const effectiveIds = new Set(getEffectiveMoveIds(ids));
    const updated = elements
      .filter(element => effectiveIds.has(element.id))
      .map(element => ({
        ...element,
        x: (element.x || 0) + delta.x,
        y: (element.y || 0) + delta.y
      }));

    onUpdateElements(updated);
  };

  const handleKeyDown = e => {
    if (selectedIds.length === 0) return;

    const target = e.target;
    const isEditingText =
      target?.tagName === "INPUT" ||
      target?.tagName === "TEXTAREA" ||
      target?.isContentEditable;

    if (isEditingText) return;

    if ((e.key === "Delete" || e.key === "Backspace") && !readOnly) {
      e.preventDefault();
      onDeleteElements(selectedIds);
      return;
    }

    const moveKeys = ["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"];
    if (!moveKeys.includes(e.key) || readOnly) return;

    e.preventDefault();

    const step = e.shiftKey ? 10 : 1;
    let deltaX = 0;
    let deltaY = 0;

    if (e.key === "ArrowUp") deltaY = -step;
    if (e.key === "ArrowDown") deltaY = step;
    if (e.key === "ArrowLeft") deltaX = -step;
    if (e.key === "ArrowRight") deltaX = step;

    moveSelection(selectedIds, deltaX, deltaY);
  };

  const getCanvasPoint = event => {
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return null;

    return {
      x: Math.max(0, Math.min(pageWidth, (event.clientX - rect.left) / zoom)),
      y: Math.max(0, Math.min(pageHeight, (event.clientY - rect.top) / zoom))
    };
  };

  const normalizeSelectionBox = box => ({
    x: Math.min(box.startX, box.currentX),
    y: Math.min(box.startY, box.currentY),
    width: Math.abs(box.currentX - box.startX),
    height: Math.abs(box.currentY - box.startY)
  });

  const elementIntersects = (element, rectangle) => {
    const x = getDisplayX(element);
    const y = getDisplayY(element);
    const width = element.width || 120;
    const height = getElementDisplayHeight(element);

    if (bandTypes.has(element.type)) {
      return (
        x >= rectangle.x &&
        x + width <= rectangle.x + rectangle.width &&
        y >= rectangle.y &&
        y + height <= rectangle.y + rectangle.height
      );
    }

    return (
      x < rectangle.x + rectangle.width &&
      x + width > rectangle.x &&
      y < rectangle.y + rectangle.height &&
      y + height > rectangle.y
    );
  };

  const handleCanvasMouseDown = event => {
    if (event.button !== 0 || event.target !== event.currentTarget) return;

    const point = getCanvasPoint(event);
    if (!point) return;

    event.currentTarget.focus();
    const nextSelectionBox = {
      startX: point.x,
      startY: point.y,
      currentX: point.x,
      currentY: point.y,
      additive: event.ctrlKey || event.metaKey,
      moved: false
    };

    selectionBoxRef.current = nextSelectionBox;
    setSelectionBox(nextSelectionBox);
  };

  const isSelecting = Boolean(selectionBox);

  useEffect(() => {
    if (!isSelecting) return undefined;

    const handleMouseMove = event => {
      const point = getCanvasPoint(event);
      if (!point) return;

      const current = selectionBoxRef.current;
      if (!current) return;

      const next = {
        ...current,
        currentX: point.x,
        currentY: point.y,
        moved:
          current.moved ||
          Math.abs(point.x - current.startX) >= 2 ||
          Math.abs(point.y - current.startY) >= 2
      };

      selectionBoxRef.current = next;
      setSelectionBox(next);
    };

    const handleMouseUp = event => {
      const point = getCanvasPoint(event);
      const current = selectionBoxRef.current;
      if (!current) return;

      const completed = point ? {
        ...current,
        currentX: point.x,
        currentY: point.y,
        moved:
          current.moved ||
          Math.abs(point.x - current.startX) >= 2 ||
          Math.abs(point.y - current.startY) >= 2
      } : current;

      if (!completed.moved) {
        onSelectionChange([]);
        selectionBoxRef.current = null;
        setSelectionBox(null);
        return;
      }

      const rectangle = normalizeSelectionBox(completed);
      const containedIds = elements
        .filter(element => elementIntersects(element, rectangle))
        .map(element => element.id);

      onSelectionChange(
        completed.additive
          ? [...new Set([...selectedIds, ...containedIds])]
          : containedIds
      );
      selectionBoxRef.current = null;
      setSelectionBox(null);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp, { once: true });

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isSelecting, elements, selectedIds]);

  const handleElementMouseDown = (event, element) => {
    if (event.button !== 0) return;

    const additive = event.ctrlKey || event.metaKey;
    const alreadySelected = selectedIdSet.has(element.id);

    if (additive && !alreadySelected) {
      onSelectionChange([...selectedIds, element.id]);
    } else if (!additive && !alreadySelected) {
      onSelectionChange([element.id]);
    }

    event.currentTarget
      .closest("[data-report-canvas='true']")
      ?.focus();
  };

  const startGroupDrag = (event, element) => {
    const dragSelection = selectedIdSet.has(element.id)
      ? selectedIds
      : (event.ctrlKey || event.metaKey)
        ? [...selectedIds, element.id]
        : [element.id];

    if (dragSelection.length <= 1) return;

    const drag = {
      anchorId: element.id,
      selectedIds: dragSelection,
      anchorX: getDisplayX(element),
      anchorY: getDisplayY(element),
      deltaX: 0,
      deltaY: 0
    };

    groupDragRef.current = drag;
    setGroupDrag(drag);
  };

  const updateGroupDrag = (element, dragData) => {
    const current = groupDragRef.current;
    if (!current || current.anchorId !== element.id) return;

    const delta = clampSelectionDelta(
      current.selectedIds,
      Math.round(dragData.x - current.anchorX),
      Math.round(dragData.y - current.anchorY)
    );
    const next = {
      ...current,
      deltaX: delta.x,
      deltaY: delta.y
    };

    groupDragRef.current = next;
    setGroupDrag(next);
  };

  const finishGroupDrag = (element, dragData) => {
    const current = groupDragRef.current;
    if (!current || current.anchorId !== element.id) return false;

    const finalDelta = clampSelectionDelta(
      current.selectedIds,
      Math.round(dragData.x - current.anchorX),
      Math.round(dragData.y - current.anchorY)
    );

    moveSelection(current.selectedIds, finalDelta.x, finalDelta.y);
    groupDragRef.current = null;
    setGroupDrag(null);
    return true;
  };

  const isMovedByGroup = element => {
    if (!groupDrag) return false;
    if (groupDrag.selectedIds.includes(element.id)) return true;

    const parentBand = findBandForChild(element);
    return Boolean(parentBand && groupDrag.selectedIds.includes(parentBand.band.id));
  };

  const getElementZIndex = element => {
    /*
    * Summary Band must always stay at the very back.
    */
    if (bandTypes.has(element.type)){
      return 0;
    }

    /*
    * Decorative elements inside the Summary Band stay above the band,
    * but below interactive fields and text.
    */
    if (
      findBandForChild(element) &&
      (
          element.type === "rectangle" ||
          element.type === "line" ||
          element.type === "image"
      )
    ) {
      return 1;
    }

    /*
    * Interactive Summary Band children stay above decorative elements.
    */
    if (findBandForChild(element)){
      return 2;
    }

    /*
    * Normal page elements outside the Summary Band.
    */
    return 1;
  };

  return (
    <Paper
      sx={{
        p: 2,
        overflow: "auto",
        bgcolor: "#f5f5f5",
        width: "100%",
        height: "100%",
        minHeight: 0,
        boxSizing: "border-box"
      }}
    >
      <Box
        sx={{
          width: pageWidth * zoom,
          height: pageHeight * zoom,
          mx: "auto"
        }}
      >
        <Box
          ref={node => {
            canvasRef.current = node;
            setNodeRef(node);
          }}
          data-report-canvas="true"
          tabIndex={0}
          onKeyDown={handleKeyDown}
          onMouseDown={handleCanvasMouseDown}
          sx={{
            outline: "none",
            position: "relative",
            width: pageWidth,
            height: pageHeight,
            bgcolor: "white",
            boxShadow: 3,
            transform: `scale(${zoom})`,
            transformOrigin: "top left"
          }}
        >
          {elements.map(element => (
            <Rnd
              key={element.id}
              disableDragging={readOnly}
              enableResizing={!readOnly && selectedIds.length <= 1}
              size={{
                width: element.width || 120,
                height: getElementDisplayHeight(element)
              }}
              position={{
                x:
                  getDisplayX(element) +
                  (isMovedByGroup(element) ? groupDrag.deltaX : 0),
                y:
                  getDisplayY(element) +
                  (isMovedByGroup(element) ? groupDrag.deltaY : 0)
              }}
              minWidth={40}
              minHeight={8}
              bounds="parent"
              scale={zoom}
              dragHandleClassName={
                element.type === "summaryBand"
                  ? "summary-band-handle"
                  : (
                      findBandForChild(element) &&
                      (
                          element.type === "rectangle" ||
                          element.type === "line" ||
                          element.type === "image"
                      )
                    )
                    ? "summary-decorative-handle"
                    : undefined
              }
               style={{
                zIndex: getElementZIndex(element),
                pointerEvents: "auto",
                overflow: "visible",
                cursor: readOnly ? "default" : "move"
              }}
              onClick={e => {
                e.stopPropagation();
                e.currentTarget.closest("[data-report-canvas='true']")?.focus();
              }}
              onMouseDown={e => handleElementMouseDown(e, element)}
              onDragStart={e => startGroupDrag(e, element)}
              onDrag={(e, d) => updateGroupDrag(element, d)}
              onDragStop={(e, d) => {
                if (finishGroupDrag(element, d)) return;

                const displayX = Math.round(d.x);
                const displayY = Math.round(d.y);

                /*
                * The Summary Band itself always remains page-level.
                */
                if (bandTypes.has(element.type)) {
                  onUpdateElement({
                    ...element,
                    band: "page",
                    x: displayX,
                    y: displayY
                  });

                  return;
                }

                const containingBand =
                  bandDefinitions
                      .map(definition => ({
                          definition,
                          band: elements.find(
                              e =>
                                  e.type === definition.type
                          )
                      }))
                      .filter(item => item.band)
                      .sort(
                          (a,b)=>
                              (b.band.width*b.band.height)-
                              (a.band.width*a.band.height)
                      )
                .find(({ band }) =>
                  band &&
                  displayX >= (band.x || 0) &&
                  displayX + (element.width || 0) <=
                    (band.x || 0) + (band.width || 0) &&
                  displayY >= (band.y || 0) &&
                  displayY + getElementDisplayHeight(element) <=
                    (band.y || 0) + (band.height || 0)
                );
                
                if (containingBand) {
                  onUpdateElement({
                    ...element,
                    band: containingBand.definition.childBand,
                    x:
                      displayX -
                      (containingBand.band.x || 0),
                    y:
                      displayY -
                      (containingBand.band.y || 0)
                  });

                  return;
                }

                /*
                * If a summary child is dragged outside the band,
                * convert it back to page coordinates.
                */
                onUpdateElement({
                  ...element,
                  band: "page",
                  x: displayX,
                  y: displayY
                });
              }}
              onResizeStop={(e, direction, ref, delta, position) => {
              const displayX = Math.round(position.x);
              const displayY = Math.round(position.y);

              const parentBandInfo =
                findBandForChild(element);

              onUpdateElement({
                ...element,
                width: Math.round(parseFloat(ref.style.width)),
                height: Math.round(parseFloat(ref.style.height)),
                x: parentBandInfo
                  ? Math.max(
                      0,
                      displayX - parentBandInfo.band.x
                    )
                  : displayX,
                              y: parentBandInfo
                  ? Math.max(
                      0,
                      displayY - parentBandInfo.band.y
                    )
                  : displayY
              });
            }}
            >
              {((element.width || 120) < 18 || getElementDisplayHeight(element) < 18) && (
                <Box
                  aria-hidden="true"
                  sx={{
                    position: "absolute",
                    left: "50%",
                    top: "50%",
                    transform: "translate(-50%, -50%)",
                    width: "100%",
                    height: "100%",
                    minWidth: 18,
                    minHeight: 18,
                    bgcolor: "transparent",
                    cursor: readOnly ? "default" : "move",
                    pointerEvents: "auto",
                    zIndex: 3
                  }}
                />
              )}
              <Box
                title={
                  (unavailableFieldPathsByElementId.get(element.id) || []).length > 0
                    ? `Unavailable report field: ${unavailableFieldPathsByElementId
                        .get(element.id)
                        .join(", ")}`
                    : undefined
                }
                data-field-availability={
                  (unavailableFieldPathsByElementId.get(element.id) || []).length > 0
                    ? "unavailable"
                    : "available"
                }
                sx={{
                  width: "100%",
                  height: "100%",
                  pointerEvents:
                  element.type === "summaryBand"
                    ? "none"
                    : (
                        findBandForChild(element) &&
                        (
                            element.type === "rectangle" ||
                            element.type === "line" ||
                            element.type === "image"
                        )
                      )
                      ? "none"
                      : "auto",
                  p:
                    element.type === "line" ||
                    element.type === "rectangle" ||
                    element.type === "transactionDetailTable" ||
                    element.type === "columnarTable" ||
                    element.type === "summaryBand" ||
                    element.type === "summaryTaxList" ||
                    element.type === "summaryWithholdingList"
                      ? 0
                      : "4px",

                  border:
                    (unavailableFieldPathsByElementId.get(element.id) || []).length > 0
                      ? "2px solid #d32f2f"
                      : element.type === "summaryBand"
                      ? selectedIdSet.has(element.id)
                        ? "2px solid #7b1fa2"
                        : "2px dashed #9c27b0"
                      : selectedIdSet.has(element.id)
                        ? "2px solid #1976d2"
                        : element.type === "line" ||
                          element.type === "rectangle"
                          ? "none"
                          : "1px dashed #aaa",

                  boxShadow:
                    (unavailableFieldPathsByElementId.get(element.id) || []).length > 0
                      ? "0 0 0 1px rgba(211, 47, 47, 0.3)"
                      : "none",

                  backgroundColor:
                      bandBackground[element.type] ??
                      resolveDesignerColor(
                          theme,
                          element.style?.backgroundColor || "transparent"
                        ),

                  display:
                    (element.type === "transactionDetailTable" || element.type === "columnarTable")
                      ? "block"
                      : "flex",                
                  fontSize: element.style?.fontSize || 12,
                  fontWeight: element.style?.bold ? "bold" : "normal",
                  color: resolveDesignerColor(
                      theme,
                      element.style?.color || "text.primary"
                  ),
                  alignItems:
                    element.style?.verticalAlign === "middle"
                      ? "center"
                      : element.style?.verticalAlign === "bottom"
                        ? "flex-end"
                        : "flex-start",
                  justifyContent:
                    element.style?.textAlign === "center"
                      ? "center"
                      : element.style?.textAlign === "right"
                        ? "flex-end"
                        : "flex-start",
                  overflow:
                    element.type === "summaryTaxList" ||
                    element.type === "summaryWithholdingList"
                      ? "visible"
                      : "hidden",
                  cursor: "move",
                  boxSizing: "border-box",
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word"
                }}
              >
                {renderElement(element, previewData, theme)}
              </Box>

              {bandTypes.has(element.type) && (
                <Box
                  className="summary-band-handle"
                  onMouseDown={e => {
                    /*
                    * Do not call stopPropagation here.
                    * react-rnd needs this event to bubble to the Rnd wrapper
                    * so dragging through dragHandleClassName can start.
                    */
                    e.currentTarget
                      .closest("[data-report-canvas='true']")
                      ?.focus();
                  }}
                  onClick={e => {
                    e.stopPropagation();
                  }}
                  sx={{
                    position: "absolute",
                    top: 2,
                    left: 2,
                    px: 1,
                    py: 0.25,
                    bgcolor: "secondary.main",
                    color: "white",
                    fontSize: 10,
                    fontWeight: "bold",
                    lineHeight: 1.4,
                    cursor: "move",
                    pointerEvents: "auto",
                    zIndex: 5,
                    userSelect: "none"
                  }}
                >
                  {
                      bandDefinitions.find(
                          b => b.type === element.type
                      )?.label
                  }
                </Box>
              )}

              {findBandForChild(element) &&
                (
                    element.type === "rectangle" ||
                    element.type === "line" ||
                    element.type === "image"
                ) && (
                  <Box
                    className="summary-decorative-handle"
                    onMouseDown={e => {
                      /*
                      * Allow the event to reach react-rnd.
                      */
                      e.currentTarget
                        .closest("[data-report-canvas='true']")
                        ?.focus();
                    }}
                    onClick={e => {
                      e.stopPropagation();
                    }}
                    sx={{
                      position: "absolute",
                      top: 2,
                      left: 2,
                      minWidth: 14,
                      height: 14,
                      px: 0.5,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      bgcolor: "rgba(25, 118, 210, 0.85)",
                      color: "white",
                      fontSize: 9,
                      lineHeight: 1,
                      borderRadius: "2px",
                      cursor: "move",
                      pointerEvents: "auto",
                      zIndex: 5,
                      userSelect: "none"
                    }}
                  >
                    {element.type === "rectangle"
                      ? "R"
                      : element.type === "line"
                        ? "L"
                        : "I"}
                  </Box>
              )}

            </Rnd>
          ))}
          {selectionBox?.moved && (() => {
            const rectangle = normalizeSelectionBox(selectionBox);
            return (
              <Box
                sx={{
                  position: "absolute",
                  left: rectangle.x,
                  top: rectangle.y,
                  width: rectangle.width,
                  height: rectangle.height,
                  border: "1px solid #1976d2",
                  bgcolor: "rgba(25, 118, 210, 0.12)",
                  pointerEvents: "none",
                  zIndex: 1000,
                  boxSizing: "border-box"
                }}
              />
            );
          })()}
        </Box>
      </Box>
    </Paper>
  );
}


  function SecureImage({ imageId }) {
    const { token } = Global_Data?.() || {};
    const [imageUrl, setImageUrl] = useState("");

    useEffect(() => {
      if (!imageId || !token) return;

      let objectUrl = "";

      axios
        .get(`${baseURL}/image/retrieve`, {
          params: { imageid: imageId },
          responseType: "blob",
          headers: {
            Authorization: `Bearer ${token}`
          }
        })
        .then(res => {
          objectUrl = URL.createObjectURL(res.data);
          setImageUrl(objectUrl);
        })
        .catch(error => {
          console.error("Failed to load secure image", error);
        });

      return () => {
        if (objectUrl) {
          URL.revokeObjectURL(objectUrl);
        }
      };
    }, [imageId, token]);

    if (!imageUrl) {
      return "Image";
    }

    return (
      <img
        src={imageUrl}
        alt=""
        style={{
          width: "100%",
          height: "100%",
          objectFit: "contain"
        }}
      />
    );
  }

function renderElement(element, previewData, theme) {

  if (element.type === "summaryTaxList") {
    const taxes = resolveRawPreviewValue(
      element.fieldPath || "summary.summ_tax",
      previewData
    );

    const rows = Array.isArray(taxes) ? taxes : [];

    if (rows.length === 0) {
      return (
        <div style={{ width: "100%" }}>
          <div
            style={{
              display: "flex",
              minHeight: element.rowHeight || 18
            }}
          >
            <div style={{ flex: 1 }}>
              Tax
            </div>

            <div
              style={{
                width: element.valueWidth || 80,
                textAlign: "right"
              }}
            >
              0.00
            </div>
          </div>
        </div>
      );
    }

    return (
      <div style={{ width: "100%" }}>
        {rows.map((tax, index) => (
          <div
            key={`${tax.sTaxCode || tax.inputValue || "tax"}-${index}`}
            style={{
              display: "flex",
              alignItems: "center",
              minHeight: element.rowHeight || 18,
              fontSize: element.style?.fontSize || 10
            }}
          >
            <div
              style={{
                flex: 1,
                paddingRight: 6,
                whiteSpace: "normal",
                wordBreak: "break-word"
              }}
            >
              {getTaxLabel(tax, element)}
            </div>

            <div
              style={{
                width: element.valueWidth || 80,
                textAlign: "right",
                whiteSpace: "nowrap"
              }}
            >
              {formatPreviewNumber(
                getTaxDisplayValue(tax, element),
                previewData
              )}
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (element.type === "summaryWithholdingList") {

    const withholdingRows =
      resolveRawPreviewValue(
        element.fieldPath || "summary.summ_withholding",
        previewData
      );

    const rows =
      Array.isArray(withholdingRows)
        ? withholdingRows
        : [];

    if (rows.length === 0) {
      return (
        <div style={{ width: "100%" }}>
          <div
            style={{
              display: "flex",
              minHeight: element.rowHeight || 18
            }}
          >
            <div style={{ flex: 1 }}>
              Withholding Tax
            </div>

            <div
              style={{
                width: element.valueWidth || 80,
                textAlign: "right"
              }}
            >
              0.00
            </div>
          </div>
        </div>
      );
    }

    return (
      <div style={{ width: "100%" }}>
        {rows.map((row, index) => (
          <div
            key={`${row.sWithholdCode || row.inputValue}-${index}`}
            style={{
              display: "flex",
              alignItems: "center",
              minHeight: element.rowHeight || 18,
              fontSize:
                element.style?.fontSize || 10
            }}
          >
            <div
              style={{
                flex: 1,
                paddingRight: 6,
                whiteSpace: "normal",
                wordBreak: "break-word"
              }}
            >
              {element.showWithholdingCode === true
              ? `${getWithholdingCode(row)}${
                  element.showWithholdingName !== false ? " - " : ""
                }`
              : ""}

            {element.showWithholdingName !== false
              ? row.sDescription ||
                getWithholdingCode(row) ||
                "Withholding Tax"
              : "Withholding Tax"}
            </div>

            <div
              style={{
                width:
                  element.valueWidth || 80,
                textAlign: "right",
                whiteSpace: "nowrap"
              }}
            >
              {formatPreviewNumber(
                row.value,
                previewData
              )}
            </div>
          </div>
        ))}
      </div>
    );
  }

  const value =
  element.fieldPath === "mainrecord.sCurrency"
    ? resolveCurrencyPreviewValue(
        element.fieldPath,
        previewData,
        element.label
      )
    : resolvePreviewValue(
        element.fieldPath,
        previewData,
        element.label
      );

  if (element.type === "field") {
      const fieldPath = element.fieldPath || element.field || "";

      if (fieldPath?.toLowerCase() === "entity.slogo") {
        const logoId = resolveRawPreviewValue(fieldPath, previewData);

        return logoId ? (
          <SecureImage imageId={logoId} />
        ) : (
          element.label || "Logo"
        );
      }

      return resolvePreviewValue(fieldPath, previewData, element.label);
    }

  if (element.type === "label" || element.type === "typography") {
    return element.text || "";
  }

  if (element.type === "line") {
    return (
      <div
        style={{
          width: "100%",
          height: "100%",
          borderTop: "1px solid #000"
        }}
      />
    );
  }

  if (element.type === "rectangle") {
    return (
      <div
        style={{
          width: "100%",
          height: "100%",
          border: "1px solid #000",
          boxSizing: "border-box"
        }}
      />
    );
  }

  if (element.type === "image") {
    return element.src ? (
      <img
        src={element.src}
        alt=""
        style={{
          width: "100%",
          height: "100%",
          objectFit: "contain"
        }}
      />
    ) : (
      "Image"
    );
  }

  if (element.type === "table") {
    return "Details Table";
  }

  if (element.type === "summaryBand") {
    return (
      <div
        style={{
          width: "100%",
          height: "100%",
          boxSizing: "border-box",
          pointerEvents: "none"
        }}
      />
    );
  }

  if (element.type === "transactionDetailTable" || element.type === "columnarTable") {
    const columns = element.columns || [];

    if (columns.length === 0) {
      return element.type === "columnarTable" ? "Columnar Table" : "Transaction Detail Table";
    }

    return (
      <table
        style={{
          width: "100%",
          height: "auto",
          borderCollapse: "collapse",
          tableLayout: "fixed",
          fontSize: element.style?.fontSize || 12
        }}
      >
        <colgroup>
          {columns.map(col => (
            <col
              key={col.fieldPath}
              style={{
                width: col.width || 120
              }}
            />
          ))}
        </colgroup>

        {element.showHeader !== false && (
          <thead>
            <tr style={{ height: element.headerHeight || 26 }}>
              {columns.map(col => (
                <th
                  key={col.fieldPath}
                  style={{
                    border: col.showHeaderCellLines !== false ? "1px solid #000" : "none",
                    height: `${element.headerHeight || 26}px`,
                    maxHeight: `${element.headerHeight || 26}px`,
                    boxSizing: "border-box",
                    textAlign: col.headerAlign || "center",
                    verticalAlign: "middle",
                    padding: 0,
                    backgroundColor: resolveDesignerColor(
                      theme,
                      col.headerBackgroundColor || "transparent"
                    ),
                    color: resolveDesignerColor(
                      theme,
                      col.headerColor || "inherit"
                    ),                    
                    fontSize: col.headerFontSize || element.style?.fontSize || 12,
                    fontWeight: col.headerBold ? "bold" : "normal"
                  }}
                >
                  <div
                    style={{
                      height: `${element.headerHeight || 26}px`,
                      maxHeight: `${element.headerHeight || 26}px`,
                      padding: 4,
                      boxSizing: "border-box",
                      overflow: "hidden",
                      whiteSpace: "normal",
                      wordBreak: "break-word",
                      lineHeight: "1.2",
                      display: "flex",
                      alignItems: "center",
                      justifyContent:
                        col.headerAlign === "right"
                          ? "flex-end"
                          : col.headerAlign === "left"
                            ? "flex-start"
                            : "center"
                    }}
                  >
                    {col.label}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
        )}

        <tbody>
          <tr style={{ height: element.rowHeight || 24 }}>
            {columns.map(col => (
              <td
                key={col.fieldPath}
                style={{
                  border: col.showDetailCellLines !== false ? "1px solid #000" : "none",
                  minHeight: `${element.rowHeight || 24}px`,
                  verticalAlign: "top",
                  overflow: "visible",
                  
                  boxSizing: "border-box",
                  textAlign: col.detailAlign || col.align || "left",
                  
                  padding: 0
                }}
              >
                <div
                    style={{
                        
                        padding: 4,
                        boxSizing: "border-box",
                        minHeight: `${element.rowHeight || 24}px`,
                        height: "auto",
                        overflow: "visible",
                        whiteSpace: "normal",
                        wordBreak: "break-word",
                        display: "block",                                                

                        fontSize:
                            col.detailFontSize ||
                            element.style?.fontSize ||
                            12,

                        fontWeight:
                            col.detailBold
                                ? "bold"
                                : "normal",

                        justifyContent:
                            col.detailAlign === "right"
                                ? "flex-end"
                                : col.detailAlign === "center"
                                ? "center"
                                : "flex-start"
                    }}
                >
                    {`{${col.label}}`}
                </div>
              </td>
            ))}
          </tr>
        </tbody>
      </table>
    );
  }
  return "";
}

  function resolveCurrencyPreviewValue(
    fieldPath,
    previewData,
    fallback = ""
  ) {
    const rawValue = resolveRawPreviewValue(
      fieldPath,
      previewData
    );

    if (rawValue === null || rawValue === undefined) {
      return fallback;
    }

    /*
    * Backend-enriched preview data.
    */
    if (
      fieldPath === "mainrecord.sCurrency" &&
      typeof rawValue === "object" &&
      !Array.isArray(rawValue)
    ) {
      return (
        rawValue.sCurrencyCode ||
        fallback
      );
    }

    /*
    * Raw transaction value:
    * PHP:1.00
    */
    if (
      fieldPath === "mainrecord.sCurrency" &&
      typeof rawValue === "string"
    ) {
      return rawValue.split(":", 2)[0].trim();
    }

    return rawValue;
  }

  function formatPreviewNumber(value, previewData) {
    if (value === null || value === undefined || value === "") {
      return "";
    }

    const number = Number(
      String(value).replace(/,/g, "")
    );

    if (Number.isNaN(number)) {
      return String(value);
    }

    const formatting = previewData?.formatting || {};

    const locale =
      formatting.locale ||
      formatting.sLocale ||
      "en-SG";

    return new Intl.NumberFormat(locale, {
      minimumFractionDigits:
        Number(formatting.decimalPlaces ?? 2),
      maximumFractionDigits:
        Number(formatting.decimalPlaces ?? 2),
      useGrouping:
        formatting.useThousandsSeparator !== false
    }).format(number);
  }

  function getTaxDisplayValue(tax, element) {
    const taxValueMode =
      element?.taxValueMode ||
      "CALCULATED_TAX";

    if (taxValueMode === "TAXABLE_AMOUNT") {
      return (
        tax?.taxBase ??
        tax?.value ??
        0
      );
    }

    return (
      tax?.calculatedTax ??
      tax?.sCalculatedValue ??
      0
    );
  }

  function getTaxLabel(tax, element) {
    const inputValue =
      String(tax?.inputValue || "").trim();

    const taxCode =
      String(
        tax?.sTaxCode ||
        inputValue
      ).trim();

    const taxName =
      String(
        tax?.sTaxName ||
        inputValue ||
        "Tax"
      ).trim();

    if (
      element?.showTaxCode === true &&
      element?.showTaxName !== false
    ) {
      return `${taxCode} - ${taxName}`;
    }

    if (element?.showTaxCode === true) {
      return taxCode || "Tax";
    }

    if (element?.showTaxName !== false) {
      return taxName || "Tax";
    }

    return "Tax";
  }

  function getWithholdingCode(row) {
    if (row?.sWithholdCode) {
      return String(row.sWithholdCode).trim();
    }

    const inputValue = String(row?.inputValue || "").trim();

    if (!inputValue) {
      return "";
    }

    return inputValue.split(":", 2)[0].trim();
  }

function resolvePreviewValue(fieldPath, previewData, fallbackLabel = "") {
  if (!fieldPath) return fallbackLabel || "";

  const parts = fieldPath.split(".");
  const section = parts[0];

  let value = previewData?.[section];

  for (let i = 1; i < parts.length; i++) {
    if (value === null || value === undefined) {
      return fallbackLabel || "";
    }

    value = value?.[parts[i]];
  }

  if (value === null || value === undefined || value === "") {
    return fallbackLabel || "";
  }

  if (typeof value === "object") {
    return fallbackLabel || "";
  }

  return String(value);
}

function resolveRawPreviewValue(fieldPath, previewData) {
  if (!fieldPath) return "";

  const parts = fieldPath.split(".");
  const section = parts[0];

  let value = previewData?.[section];

  for (let i = 1; i < parts.length; i++) {
    if (value === null || value === undefined) return "";
    value = value?.[parts[i]];
  }

  return value ?? "";
}
